/** WHO UV index scale reference maximum. */
export const UV_INDEX_GRADIENT_MAX = 11;

/** Figma UV chart fill opacity (`paint0_linear_9246_10004`). */
export const UV_CHART_FILL_OPACITY = 0.4;

export interface UvIndexColourStop {
  value: number;
  rgb: readonly [number, number, number];
}

/**
 * UV chart palette from Figma node 9246:10004 — green → tan → red across the
 * 0–11 index scale.
 */
export const UV_INDEX_COLOUR_STOPS: UvIndexColourStop[] = [
  { value: 0, rgb: [201, 255, 180] }, // #C9FFB4
  { value: 5.5, rgb: [190, 150, 95] }, // #BE965F
  { value: UV_INDEX_GRADIENT_MAX, rgb: [178, 10, 10] }, // #B20A0A
];

const interpolateRgb = (
  from: readonly [number, number, number],
  to: readonly [number, number, number],
  ratio: number,
): string => {
  const red = Math.round(from[0] + ratio * (to[0] - from[0]));
  const green = Math.round(from[1] + ratio * (to[1] - from[1]));
  const blue = Math.round(from[2] + ratio * (to[2] - from[2]));
  return `rgb(${red}, ${green}, ${blue})`;
};

const toRgba = (
  rgb: readonly [number, number, number],
  opacity: number,
): string => `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;

export const getUvIndexColorForStops = (
  uvIndex: number,
  stops: UvIndexColourStop[] = UV_INDEX_COLOUR_STOPS,
): string => {
  if (stops.length === 0) {
    return 'currentColor';
  }

  if (uvIndex <= stops[0].value) {
    const [r, g, b] = stops[0].rgb;
    return `rgb(${r}, ${g}, ${b})`;
  }

  const lastStop = stops[stops.length - 1];
  if (uvIndex >= lastStop.value) {
    const [r, g, b] = lastStop.rgb;
    return `rgb(${r}, ${g}, ${b})`;
  }

  for (let index = 0; index < stops.length - 1; index += 1) {
    const from = stops[index];
    const to = stops[index + 1];

    if (uvIndex >= from.value && uvIndex <= to.value) {
      const span = to.value - from.value;
      const ratio = span === 0 ? 0 : (uvIndex - from.value) / span;
      return interpolateRgb(from.rgb, to.rgb, ratio);
    }
  }

  const [r, g, b] = lastStop.rgb;
  return `rgb(${r}, ${g}, ${b})`;
};

export const getUvIndexColor = (uvIndex: number): string =>
  getUvIndexColorForStops(uvIndex);

/** Mix UV palette colours toward black for the chart line stroke. */
const UV_LINE_DARKEN = 0.55;

const darkenRgbTuple = (
  rgb: readonly [number, number, number],
  amount: number,
): readonly [number, number, number] => [
  Math.round(rgb[0] * (1 - amount)),
  Math.round(rgb[1] * (1 - amount)),
  Math.round(rgb[2] * (1 - amount)),
];

/** Darkened UV ramp used for the chart line (fill keeps the Figma opacity). */
export const UV_INDEX_LINE_COLOUR_STOPS: UvIndexColourStop[] =
  UV_INDEX_COLOUR_STOPS.map((stop) => ({
    value: stop.value,
    rgb: darkenRgbTuple(stop.rgb, UV_LINE_DARKEN),
  }));

export const getUvIndexLineColor = (uvIndex: number): string =>
  getUvIndexColorForStops(uvIndex, UV_INDEX_LINE_COLOUR_STOPS);

export interface UvFillGradientStop {
  offset: string;
  color: string;
}

/** Figma fill gradient offsets (bottom → top in the area bounding box). */
export const UV_CHART_FILL_GRADIENT_OFFSETS = ['0%', '50%', '100%'] as const;

/**
 * Area fill gradient matching Figma `paint0_linear_9246_10004`: three stops at
 * 0 / 50 / 100% with 40% opacity.
 */
export const buildUvFillGradientStops = (): UvFillGradientStop[] =>
  UV_INDEX_COLOUR_STOPS.map((stop, index) => ({
    offset: UV_CHART_FILL_GRADIENT_OFFSETS[index],
    color: toRgba(stop.rgb, UV_CHART_FILL_OPACITY),
  }));

/** Line stroke gradient using the same three-stop layout at full opacity. */
export const buildUvLineGradientStops = (): UvFillGradientStop[] =>
  UV_INDEX_COLOUR_STOPS.map((stop, index) => ({
    offset: UV_CHART_FILL_GRADIENT_OFFSETS[index],
    color: getUvIndexLineColor(stop.value),
  }));

/** Minimum headroom above the day's peak so the curve does not touch the plot top. */
const UV_INDEX_Y_DOMAIN_HEADROOM = 0.5;

/** Y-axis domain anchored at zero, scaled to the day's peak so the gradient is visible. */
export const getUvIndexYDomain = (values: number[]): [number, number] => {
  if (values.length === 0) {
    return [0, 1];
  }

  const dataMax = Math.max(...values);
  if (dataMax <= 0) {
    return [0, 1];
  }

  return [0, Math.max(1, Math.ceil(dataMax + UV_INDEX_Y_DOMAIN_HEADROOM))];
};
